/** The agents table on Home and on /agents. Each row opens the agent. */

import { Link } from "react-router";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Pill } from "@/components/ui/pill";
import { isLive } from "@/features/experiments/status";
import { ExperimentStatusPill } from "@/features/experiments/status-pill";
import { formatCount, formatRelative, formatScore } from "@/lib/format";
import type { Agent } from "./types";

/** The newest experiment of any status: "Testing  [Failed]  5 Oct". */
function LastExperimentCell({ stats }: { stats: Agent["stats"] }) {
  const last = stats.last_experiment;
  const othersRunning = stats.running - (last && isLive(last.status) ? 1 : 0);
  if (!last) {
    return stats.running > 0 ? (
      <Pill tone="run" dot>
        {stats.running} running
      </Pill>
    ) : (
      <span className="text-muted">No experiments yet</span>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Link
        to={`/experiments/${last.id}`}
        className="max-w-[220px] truncate text-[13px] whitespace-nowrap text-body hover:underline"
      >
        {last.name}
      </Link>
      <span className="shrink-0">
        <ExperimentStatusPill experiment={last} />
      </span>
      {othersRunning > 0 && <span className="shrink-0 text-xs text-muted">+{othersRunning} running</span>}
      <span className="shrink-0 text-xs whitespace-nowrap text-muted">{formatRelative(last.at)}</span>
    </div>
  );
}

/** "Orchestrator · localhost:8000": where the agent lives, in one muted line. */
function whereLabel(agent: Agent): string {
  return `${agent.connector_label} · ${agent.base_url.replace(/^https?:\/\//, "")}`;
}

const COLUMNS: Column<Agent>[] = [
  {
    header: "Agent",
    className: "max-w-[280px]",
    cell: (agent) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <Link
          to={`/agents/${agent.id}`}
          title={agent.name}
          className="truncate text-sm font-semibold whitespace-nowrap hover:underline"
        >
          {agent.name}
        </Link>
        <span className="truncate text-xs whitespace-nowrap text-muted" title={agent.base_url}>
          {whereLabel(agent)}
        </span>
      </div>
    ),
  },
  { header: "Last experiment", cell: (agent) => <LastExperimentCell stats={agent.stats} /> },
  {
    header: "Latest score",
    num: true,
    className: "text-[15px]",
    cell: (a) => formatScore(a.stats.last_score),
  },
  { header: "Experiments", num: true, cell: (a) => formatCount(a.stats.experiments) },
];

export const AgentsTable = ({ agents }: { agents: Agent[] }) => <DataTable columns={COLUMNS} rows={agents} />;
